import { z } from "zod";

import { DataConnectionTypeLiteral } from "../../dataConnectionTypes.js";
import { DisplayTableOutput } from "../../display-table/index.js";
import { CellTypeRuntype } from "../../esDocTypeEnum.js";
import {
  AgentChatThreadId,
  CellId,
  DataConnectionId,
  DataSourceTableId,
  FileId,
  HexId,
  OrgGuideFileId,
  OrgGuideFileVersionId,
  SemanticDatasetId,
  SemanticProjectFileId,
  SemanticProjectFilePath,
  SemanticProjectId,
  SemanticViewId,
  StaticCellId,
  TaoProjectRefId,
} from "../../idTypeBrands.js";
import {
  MagicSupportedImageMimeTypes,
  MentionedDataframeName,
} from "../../MagicTypes.js";
import { getNormalEnum } from "../../pseudoEnums.js";

import { MessageAskUserBlock } from "./askUser.js";
import { AgentChatThreadType, TaoEntryTileLiteral } from "./threads";
import { ToolIconLiteral } from "./tools/icons.js";
import {
  DataPreviewColumn,
  JoinEdge,
  KnowledgeSearchDisplayAtom,
  LinkItem,
  LocalJoinGraph,
  RelatedProject,
  SemanticColumnItem,
  SemanticProjectItem,
  SemanticResourceItem,
  TableItem,
} from "./tools/sharedTypes.js";

export const MessageRoleLiteral = z.enum(["user", "agent"]);
export type MessageRole = z.infer<typeof MessageRoleLiteral>;
export const MessageRole = getNormalEnum(MessageRoleLiteral);
const AssistantPhaseLiteral = z.enum(["commentary", "final_answer"]);
export const ConversationMessageRoleLiteral = z.enum([
  "user",
  "agent",
  "assistant",
  "system",
]);
export type ConversationMessageRole = z.infer<
  typeof ConversationMessageRoleLiteral
>;

export const MessageThinkingBlock = z.object({
  type: z.literal("thinking"),
  text: z.string(),
  providerMetadata: z
    .object({
      signature: z.string().optional(),

      toolCallId: z.string().optional(),
      toolCallIds: z.array(z.string()).optional(),
      phase: AssistantPhaseLiteral.optional(),
    })
    .optional(),
});
export type MessageThinkingBlock = z.infer<typeof MessageThinkingBlock>;

/**
 * A block that represents a text/markdown message.
 */
export const MessageTextBlock = z.object({
  type: z.literal("text"),
  text: z.string(),
  entryTile: TaoEntryTileLiteral.optional(),
  providerMetadata: z
    .object({
      toolCallId: z.string().optional(),
      toolCallIds: z.array(z.string()).optional(),
      phase: AssistantPhaseLiteral.optional(),
    })
    .optional(),
});
export type MessageTextBlock = z.infer<typeof MessageTextBlock>;

/**
 * Transient progress block shown while provider-native server compaction runs.
 */
export const MessageCompressionTriggeredBlock = z.object({
  type: z.literal("compression_triggered"),
});
export type MessageCompressionTriggeredBlock = z.infer<
  typeof MessageCompressionTriggeredBlock
>;

export const LlmProviderLiteral = z.enum(["anthropic", "openai"]);
export type LlmProvider = z.infer<typeof LlmProviderLiteral>;

/**
 * Identifies the mechanism that produced a server-side compaction payload.
 * "anthropic"/"openai" are provider-native compaction; "manual" is Hex's own
 * server-side summarization. This is intentionally distinct from
 * {@link LlmProviderLiteral} (which names real LLM API providers) because
 * "manual" is not an LLM provider.
 */
export const CompactionProviderLiteral = z.enum([
  "anthropic",
  "openai",
  "manual",
]);
export type CompactionProvider = z.infer<typeof CompactionProviderLiteral>;

/**
 * Server-side compaction payload to carry forward in history.
 * Anthropic/OpenAI payloads are provider-native and opaque; manual payloads are
 * plain-text summaries replayed via the chat-completions conformer.
 */
export const MessageServerCompactionBlock = z.object({
  type: z.literal("server_compaction"),
  // Identifies which compaction mechanism produced this block.
  provider: CompactionProviderLiteral,
  content: z.string(),
});
export type MessageServerCompactionBlock = z.infer<
  typeof MessageServerCompactionBlock
>;

export const MessageToolCallBlock = z.object({
  type: z.literal("tool_call"),
  toolName: z.string(),
  arguments: z.string(),
  label: z.string().optional(),
  icon: ToolIconLiteral.optional(),
  providerMetadata: z
    .object({
      toolCallId: z.string().optional(),
      provider: LlmProviderLiteral.optional(),
      phase: AssistantPhaseLiteral.optional(),
      openaiFunctionCallNamespace: z.string().optional(),
    })
    .optional(),
});
export type MessageToolCallBlock = z.infer<typeof MessageToolCallBlock>;

export const ToolResultTypes = z.enum([
  "string",
  "activity",
  "table_list",
  "semantic-column-list",
  "knowledge-search-list",
  "semantic-project-list",
  "semantic-resource-list",
  "display_table",
  "error",
  "image",
  "related-projects",
  "semantic-model",
  "semantic-project-file",
  "look-up-project",
  "empty",
  "sql-and-preview-table",
  "list-of-links",
  "cancelled",
  "cell-list",
  "image-file",
  "text-file",
  "org-guide-file-list",
  "read-guide",
  "hidden",
  "mcp-tool-call",
  "data_connection_list",
  "suggest-review",
  "user-question",
  "data-app-file-paths",
  "data-app-grep-hits",
  "file-diff",
  "tao-navigation",
  "dag-artifact-previews",
  "dag-structure",
]);
export type ToolResultType = z.infer<typeof ToolResultTypes>;

/**
 * Base type for all tool results with optional label field
 */
const BaseToolResult = z.object({
  type: ToolResultTypes,
  label: z.string(),
  icon: ToolIconLiteral,
  /**
   * Optional muted text shown right-aligned in the tool result header,
   * useful for counts/badges (e.g. "12 files", "no matches")
   */
  secondaryLabel: z.string().optional(),
});

const EmptyResult = BaseToolResult.extend({
  type: z.literal("empty"),
  content: z.null(),
});
export type EmptyResult = z.infer<typeof EmptyResult>;

export const ToolResultSourceLiteral = z.enum([
  "browserbase",
  "screenshot_service",
  /** @deprecated Client iframe capture removed; retained for persisted tool results. */
  "client",
]);
export type ToolResultSource = z.infer<typeof ToolResultSourceLiteral>;

const StringResult = BaseToolResult.extend({
  type: z.literal("string"),
  content: z.string(),
  source: ToolResultSourceLiteral.optional(),
});
export type StringResult = z.infer<typeof StringResult>;

/** Text with a dedicated label and Markdown body for the refreshed activity timeline. */
const ActivityResult = BaseToolResult.extend({
  type: z.literal("activity"),
  content: z.string(),
  activity: z.object({ label: z.string(), content: z.string() }),
});
export type ActivityResult = z.infer<typeof ActivityResult>;

const TableListResult = BaseToolResult.extend({
  type: z.literal("table_list"),
  tables: z.array(TableItem),
});
export type TableListResult = z.infer<typeof TableListResult>;

export const ErrorResult = BaseToolResult.extend({
  type: z.literal("error"),
  message: z.union([z.string(), z.null()]),
  trace: z.union([z.string(), z.null()]),
});
export type ErrorResult = z.infer<typeof ErrorResult>;

export const CancelledResult = BaseToolResult.extend({
  type: z.literal("cancelled"),
});
export type CancelledResult = z.infer<typeof CancelledResult>;

export const DisplayTableResult = BaseToolResult.extend({
  type: z.literal("display_table"),
  rows: DisplayTableOutput,
});
export type DisplayTableResult = z.infer<typeof DisplayTableResult>;

export const ImageResult = BaseToolResult.extend({
  type: z.literal("image"),
  mimeType: MagicSupportedImageMimeTypes,
  dataUri: z.string(),
  source: ToolResultSourceLiteral.optional(),
});
export type ImageResult = z.infer<typeof ImageResult>;

const RelatedProjectsResult = BaseToolResult.extend({
  type: z.literal("related-projects"),
  content: z.object({
    hexes: z.array(RelatedProject),
  }),
});
export type RelatedProjectsResult = z.infer<typeof RelatedProjectsResult>;

const LinkListResult = BaseToolResult.extend({
  type: z.literal("list-of-links"),
  content: z.object({
    links: z.array(LinkItem),
  }),
});
export type LinkListResult = z.infer<typeof LinkListResult>;

const SemanticColumnListResult = BaseToolResult.extend({
  type: z.literal("semantic-column-list"),
  content: z.array(SemanticColumnItem),
  /** Kind of the inspected resource; absent in older persisted results. */
  resourceType: z.enum(["dataset", "view"]).optional(),
  /** Addressable name of the inspected resource; absent in older persisted results. */
  resourceName: z.string().optional(),
  /** Human-readable title of the inspected semantic dataset or view. */
  semanticResourceTitle: z.string().or(z.null()).optional(),
  /** Optional markdown text to display (e.g., data preview) - kept for backwards compatibility */
  additionalMarkdown: z.string().optional(),
  /** Structured local join graph for Mode 3 visualization */
  localJoinGraph: LocalJoinGraph.optional(),
  /** Structured data preview rows for rich table rendering */
  dataPreviewRows: z.array(DataPreviewColumn).optional(),
  /** Total row count returned by the preview query */
  totalRowCount: z.number().optional(),
});
export type SemanticColumnListResult = z.infer<typeof SemanticColumnListResult>;

const KnowledgeSearchListResult = BaseToolResult.extend({
  type: z.literal("knowledge-search-list"),
  content: z.object({
    markdown: z.string(),
    queries: z.array(z.string()),
    resultCount: z.number(),
    results: z.array(KnowledgeSearchDisplayAtom).optional(),
  }),
});
export type KnowledgeSearchListResult = z.infer<
  typeof KnowledgeSearchListResult
>;

const SemanticProjectListResult = BaseToolResult.extend({
  type: z.literal("semantic-project-list"),
  content: z.array(SemanticProjectItem),
});
export type SemanticProjectListResult = z.infer<
  typeof SemanticProjectListResult
>;

const SemanticResourceListResult = BaseToolResult.extend({
  type: z.literal("semantic-resource-list"),
  content: z.array(SemanticResourceItem),
  /** The project these resources belong to */
  semanticProjectId: SemanticProjectId,
  semanticProjectName: z.string(),
  /** Optional markdown text to display (e.g., join graph information) */
  additionalMarkdown: z.string().optional(),
  /** Structured join edges for full project join graph visualization (Mode 2) */
  joinEdges: z.array(JoinEdge).optional(),
});
export type SemanticResourceListResult = z.infer<
  typeof SemanticResourceListResult
>;

/** @deprecated - No longer used, replaced by `SemanticProjectFileResult`. */
const SemanticDatasetResult = BaseToolResult.extend({
  type: z.literal("semantic-model"),
  content: z.object({
    semanticDatasetId: SemanticDatasetId,
    semanticDatasetName: z.string(),
    originalYml: z.string().optional(),
    modifiedYml: z.string().optional(),
  }),
});
export type SemanticDatasetResult = z.infer<typeof SemanticDatasetResult>;

const SemanticProjectFileResult = BaseToolResult.extend({
  type: z.literal("semantic-project-file"),
  content: z.object({
    semanticProjectFileId: SemanticProjectFileId,
    filePath: SemanticProjectFilePath,
    contentsText: z
      .object({
        original: z.string().optional(),
        modified: z.string().optional(),
      })
      .optional(),
  }),
});
export type SemanticProjectFileResult = z.infer<
  typeof SemanticProjectFileResult
>;

const LookUpProjectResult = BaseToolResult.extend({
  type: z.literal("look-up-project"),
  hexId: HexId.optional(),
  content: z.object({
    project: z.object({
      title: z.string(),
      description: z.string().or(z.null()).optional(),
    }),
    cells: z.array(
      z.object({
        staticCellId: StaticCellId,
        /** @deprecated Use staticCellId instead. */
        cellId: CellId.or(z.null()).optional(),
        label: z.string(),
        cellType: CellTypeRuntype,
        contents: z.string(),
      }),
    ),
  }),
});
export type LookUpProjectResult = z.infer<typeof LookUpProjectResult>;

const McpToolCallResult = BaseToolResult.extend({
  type: z.literal("mcp-tool-call"),
  request: z.unknown(),
  toolName: z.string(),
  response: z.unknown(),
});

export type McpToolCallResult = z.infer<typeof McpToolCallResult>;

const SQLAndPreviewTableResult = BaseToolResult.extend({
  type: z.literal("sql-and-preview-table"),
  sqlContent: z.string(),
  totalRowCount: z.number(),
  rows: z.array(DataPreviewColumn),
  totalRowCountOfPreview: z.boolean().optional(),
  dataConnectionName: z.string().optional(),
  dataConnectionType: DataConnectionTypeLiteral.optional(),
  /** Set when `rows` holds only the first of this many columns. */
  totalColumnCount: z.number().optional(),
});
export type SQLAndPreviewTableResult = z.infer<typeof SQLAndPreviewTableResult>;

/** One code project cell's result, as a table preview or as text. */
export const DagArtifactPreview = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("table"),
    name: z.string(),
    columns: z.array(DataPreviewColumn),
    totalRowCount: z.number(),
    totalColumnCount: z.number(),
  }),
  z.object({ kind: z.literal("value"), name: z.string(), value: z.string() }),
  z.object({ kind: z.literal("error"), name: z.string(), message: z.string() }),
]);
export type DagArtifactPreview = z.infer<typeof DagArtifactPreview>;

/** The cells a code project run computed, each previewed in place. */
export const DagArtifactPreviewsResult = BaseToolResult.extend({
  type: z.literal("dag-artifact-previews"),
  artifacts: z.array(DagArtifactPreview),
  /** Cells left out of `artifacts` to bound the stored result. */
  omittedCount: z.number(),
});
export type DagArtifactPreviewsResult = z.infer<
  typeof DagArtifactPreviewsResult
>;

/** A code project's cells and parameters, as read by the agent. */
export const DagStructureResult = BaseToolResult.extend({
  type: z.literal("dag-structure"),
  /** `languageId` is the hexdag document language, such as "sql" or "python". */
  cells: z.array(z.object({ name: z.string(), languageId: z.string() })),
  parameters: z.array(z.object({ name: z.string(), defaultValue: z.string() })),
  /** Cells and parameters left out of the lists to bound the stored result. */
  omittedCellCount: z.number(),
  omittedParameterCount: z.number(),
});
export type DagStructureResult = z.infer<typeof DagStructureResult>;

/**
 * Displays a list of clickable cells.
 */
export const CellListResult = BaseToolResult.extend({
  type: z.literal("cell-list"),
  content: z.object({
    staticCellIds: z.array(StaticCellId).optional(),
    /** @deprecated Use staticCellIds instead. */
    cellIds: z.array(CellId).optional(),
  }),
});
export type CellListResult = z.infer<typeof CellListResult>;

const ImageFileResult = BaseToolResult.extend({
  type: z.literal("image-file"),
  content: z.object({
    fileId: FileId,
    fileName: z.string(),
  }),
});
export type ImageFileResult = z.infer<typeof ImageFileResult>;

const TextFileResult = BaseToolResult.extend({
  type: z.literal("text-file"),
  content: z.object({
    fileId: FileId,
    fileName: z.string(),
  }),
});
export type TextFileResult = z.infer<typeof TextFileResult>;

const OrgGuideFileListResult = BaseToolResult.extend({
  type: z.literal("org-guide-file-list"),
  orgGuideFiles: z.array(
    z.object({
      filePath: z.string(),
      /** The guide's frontmatter `name`, when it declares one. */
      frontmatterName: z.string().optional(),
      /** The guide's frontmatter `description`, when it declares one. */
      frontmatterDescription: z.string().optional(),
      sectionPath: z.string().optional(),
      matchedSectionPaths: z.array(z.string()).optional(),
      orgGuideFileId: OrgGuideFileId,
      // only link the content by `orgGuideFileVersionId` since we don't want
      // to store big blobs of text extra times in the db, and versions are immutable
      orgGuideFileVersionId: OrgGuideFileVersionId,
    }),
  ),
});
export type OrgGuideFileListResult = z.infer<typeof OrgGuideFileListResult>;

const ReadGuideResult = BaseToolResult.extend({
  type: z.literal("read-guide"),
  content: z.string(),
  orgGuideFileId: OrgGuideFileId.optional(),
  orgGuideFileVersionId: OrgGuideFileVersionId.optional(),
});
export type ReadGuideResult = z.infer<typeof ReadGuideResult>;

const DataConnectionListResult = BaseToolResult.extend({
  type: z.literal("data_connection_list"),
  content: z.array(DataConnectionId),
});
export type DataConnectionListResult = z.infer<typeof DataConnectionListResult>;

const HiddenResult = z.object({
  type: z.literal("hidden"),
});
export type HiddenResult = z.infer<typeof HiddenResult>;

/** Use this for tools that don't need to display anything in the UI. */
export const HIDDEN_RESULT: HiddenResult = { type: "hidden" };

export const SuggestReviewResult = BaseToolResult.extend({
  type: z.literal("suggest-review"),
});
export type SuggestReviewResult = z.infer<typeof SuggestReviewResult>;

export const MultiChoiceQuestionResult = BaseToolResult.extend({
  type: z.literal("user-question"),
  content: z.object({
    question: z.string(),
    answers: z.array(z.string()),
  }),
});
export type MultiChoiceQuestionResult = z.infer<
  typeof MultiChoiceQuestionResult
>;

/**
 * Tool result that resolved to a list of data app file paths. Used by data
 * app file tools (write/edit/read/move/copy and their multi-file variants)
 * to render a clickable header / row per path that opens the file in the
 * App Builder source panel. Carries paths directly so the client never has
 * to parse them out of labels or arguments.
 */
export const DataAppFilePathsResult = BaseToolResult.extend({
  type: z.literal("data-app-file-paths"),
  /** Paths the row should treat as click targets, in display order. */
  paths: z.array(z.string()),
});
export type DataAppFilePathsResult = z.infer<typeof DataAppFilePathsResult>;

export const TaoNavigationView = z.enum([
  "chat",
  "code",
  "preview",
  "files",
  "split",
]);
export type TaoNavigationView = z.infer<typeof TaoNavigationView>;

/**
 * Header tab labels of the project page. The client renders these and the
 * agent's prompts name tabs with them, so a rename reaches both at once.
 * Split is a layout of two tabs rather than a tab, so it has no label here.
 */
export const TAO_PROJECT_TAB_LABELS: Record<
  Exclude<TaoNavigationView, "split">,
  string
> = {
  chat: "Agent",
  code: "Notebook",
  preview: "App",
  files: "Files",
};

export const TAO_NAVIGATION_TARGET_MAX_CHARS = 300;
export const TaoProjectUserView = z.object({
  tab: TaoNavigationView,
  filePath: z
    .string()
    .trim()
    .min(1)
    .max(TAO_NAVIGATION_TARGET_MAX_CHARS)
    .nullish()
    .catch(null),
  inspectCellName: z
    .string()
    .trim()
    .min(1)
    .max(TAO_NAVIGATION_TARGET_MAX_CHARS)
    .nullish()
    .catch(null),
});
export type TaoProjectUserView = z.infer<typeof TaoProjectUserView>;

export const TaoNavigationTarget = z.union([
  z.object({ kind: z.literal("view"), view: TaoNavigationView }),
  z.object({ kind: z.literal("file"), path: z.string() }),
  z.object({ kind: z.literal("cell"), name: z.string() }),
]);
export type TaoNavigationTarget = z.infer<typeof TaoNavigationTarget>;

export const TaoNavigationResult = BaseToolResult.extend({
  type: z.literal("tao-navigation"),
  projectId: z.string(),
  taoProjectRefId: TaoProjectRefId.nullish(),
  target: TaoNavigationTarget,
});
export type TaoNavigationResult = z.infer<typeof TaoNavigationResult>;

/**
 * Tool result for `GrepDataAppFiles`. Carries the search pattern plus a
 * structured per-file hit list so the client can render search-term tags in
 * the title and a clickable row per matched file (with match count) without
 * having to re-parse a stringified body.
 */
export const DataAppGrepHitsResult = BaseToolResult.extend({
  type: z.literal("data-app-grep-hits"),
  /** Original regex pattern the agent searched for (alternation-split client-side into tags). */
  pattern: z.string(),
  hits: z.array(
    z.object({
      path: z.string(),
      /** Match count for the file. `0` is allowed for `filesOnly` mode where we don't enumerate matches. */
      matchCount: z.number(),
    }),
  ),
});
export type DataAppGrepHitsResult = z.infer<typeof DataAppGrepHitsResult>;

export const AiderDiffDisplayLineKind = z.enum(["added", "context", "removed"]);
export type AiderDiffDisplayLineKind = z.infer<typeof AiderDiffDisplayLineKind>;

export const AiderDiffDisplayLine = z.object({
  kind: AiderDiffDisplayLineKind,
  text: z.string(),
  newLineNumber: z.number().optional(),
  oldLineNumber: z.number().optional(),
});
export type AiderDiffDisplayLine = z.infer<typeof AiderDiffDisplayLine>;

export const AiderDiffHunk = z.object({
  displayLines: z.array(AiderDiffDisplayLine),
});
export type AiderDiffHunk = z.infer<typeof AiderDiffHunk>;

/**
 * Structured unified diff lines for agent file-edit tool results.
 */
export const FileDiffToolResult = BaseToolResult.extend({
  type: z.literal("file-diff"),
  files: z.array(
    z.object({
      path: z.string(),
      hunks: z.array(AiderDiffHunk),
    }),
  ),
});
export type FileDiffToolResult = z.infer<typeof FileDiffToolResult>;

export const ToolResult = z.union([
  EmptyResult,
  StringResult,
  ActivityResult,
  TableListResult,
  DisplayTableResult,
  ErrorResult,
  CancelledResult,
  ImageResult,
  RelatedProjectsResult,
  LinkListResult,
  SemanticColumnListResult,
  KnowledgeSearchListResult,
  SemanticProjectListResult,
  SemanticResourceListResult,
  SemanticDatasetResult,
  SemanticProjectFileResult,
  LookUpProjectResult,
  SQLAndPreviewTableResult,
  McpToolCallResult,
  CellListResult,
  ImageFileResult,
  TextFileResult,
  OrgGuideFileListResult,
  DataConnectionListResult,
  HiddenResult,
  ReadGuideResult,
  SuggestReviewResult,
  MultiChoiceQuestionResult,
  DataAppFilePathsResult,
  TaoNavigationResult,
  DataAppGrepHitsResult,
  FileDiffToolResult,
  DagArtifactPreviewsResult,
  DagStructureResult,
]);
export type ToolResult = z.infer<typeof ToolResult>;

export type DisplayableToolResult = Exclude<ToolResult, HiddenResult>;

export const isDisplayableToolResult = (
  result: ToolResult,
): result is DisplayableToolResult => result.type !== "hidden";

const OpenAIReasoningReplayItem = z.object({
  id: z.string(),
  encryptedContent: z.string(),
});

// NOTE: In January 2026, we decided to migrate the agent framework to use static cell IDs,
// instead of cell IDs. The entity ref system always stored staticCellIds, but e.g. tool finished
// and what would be provided to the frontend would be cellIds. This caused problems for the
// chat with app agent - because the cell IDs were tied to the latest published hex version,
// inline cell links or cell-based tool results in old chats would break when the app was republished.
//
// There are still historical threads that use cellId, and those will need to be handled by the
// frontend in perpetuity. Going forward, all tool results will contain staticCellIds, not cellIds

export const MessageToolResultBlock = z.object({
  type: z.literal("tool_result"),
  toolName: z.string(),
  result: ToolResult,
  // Arguments are optional for backwards compatibility with old tool results. We can make this required after > 90 days.
  arguments: z.string().or(z.null()).optional(),
  staticCellId: StaticCellId.or(z.null()).optional(),
  providerMetadata: z
    .object({
      toolCallId: z.string().optional(),
      provider: LlmProviderLiteral.optional(),
      phase: AssistantPhaseLiteral.optional(),
      openaiReasoningEncryptedContents: z
        .array(OpenAIReasoningReplayItem)
        .optional(),
      openaiFunctionCallNamespace: z.string().optional(),
    })
    .optional(),
  /** @deprecated Use staticCellId instead. */
  cellId: CellId.or(z.null()).optional(),
});
export type MessageToolResultBlock = z.infer<typeof MessageToolResultBlock>;

export const CellMentionBlock = z.object({
  type: z.literal("cell_mention"),
  // TODO(ACORE-116): add staticCellId in a follow up PR
  cellId: CellId,
});
export type CellMentionBlock = z.infer<typeof CellMentionBlock>;

/**
 * Cell mention that points at a cell in a *different* hex than the thread's
 * working hex — specifically, a cell from the published app the user was
 * viewing when they sent the message (ask-from-app). Persisted separately
 * from `cell_mention` so renderers can resolve the label/cellId through the
 * viewing-app's Redux slice (rather than the chat's working hex), and so the
 * server can render it with a project-scoped ref.
 */
export const ViewingAppCellMentionBlock = z.object({
  type: z.literal("viewing_app_cell_mention"),
  cellId: CellId,
});
export type ViewingAppCellMentionBlock = z.infer<
  typeof ViewingAppCellMentionBlock
>;

export const MAX_APP_ELEMENT_DATA_FILE_NAME_CHARS = 128;
const AppElementDataFileName = z
  .string()
  .refine(
    (dataFileName) =>
      dataFileName.length > 0 &&
      dataFileName.length <= MAX_APP_ELEMENT_DATA_FILE_NAME_CHARS,
    {
      message: `dataFileName must be between 1 and ${MAX_APP_ELEMENT_DATA_FILE_NAME_CHARS} characters`,
    },
  );

/**
 * A chart element the user selected in a generative data app's edit mode
 * (component + region, optionally narrowed by instanceId/seriesKey). Serialized
 * subset of the iframe ChartElementSelection — geometry and render-closure
 * context are dropped since the agent only needs identity + current values.
 *
 * `kind` is optional here and nowhere else: rows persisted before text
 * selections existed carry no discriminator, and a chart is what they are.
 */
export const MAX_TAO_APP_SELECTION_COUNT = 10;
export const MAX_TAO_APP_SELECTION_JSON_CHARS = 16_384;
export const MAX_TAO_APP_SELECTION_PATH_CHARS = 512;
const AppSelectionPath = z.string().max(MAX_TAO_APP_SELECTION_PATH_CHARS);
/** Code projects only: the react cell the element renders in, which names its source. */
const AppSelectionReactCell = z.string().max(2048);

export const SerializedChartElementSelection = z.object({
  appPath: AppSelectionPath.optional(),
  kind: z.literal("chart").optional(),
  component: z.string(),
  label: z.string().optional(),
  reactCell: AppSelectionReactCell.optional(),
  instanceId: z.string().optional(),
  region: z.string(),
  seriesKey: z.string().optional(),
  currentValues: z.record(z.string(), z.unknown()).optional(),
  dataFileName: AppElementDataFileName.optional(),
  dataPreview: z.string().optional(),
  dataPreviewTotalChars: z.number().optional(),
});
export type SerializedChartElementSelection = z.infer<
  typeof SerializedChartElementSelection
>;

/**
 * A text element the user selected in a generative app. `elementType` is the
 * tag ("H1") and `label` the snippet — together they say which element this
 * was, which is all the agent needs.
 */
export const SerializedTextElementSelection = z.object({
  appPath: AppSelectionPath.optional(),
  kind: z.literal("text"),
  elementType: z.string(),
  label: z.string().optional(),
  reactCell: AppSelectionReactCell.optional(),
});
export type SerializedTextElementSelection = z.infer<
  typeof SerializedTextElementSelection
>;

/**
 * Text arm first: a legacy chart row has no `kind` and fails it immediately,
 * while a text row lacks the `component`/`region` the chart arm requires.
 */
export const SerializedElementSelection = z.union([
  SerializedTextElementSelection,
  SerializedChartElementSelection,
]);
export type SerializedElementSelection = z.infer<
  typeof SerializedElementSelection
>;

export const MessageAppElementMentionBlock = z.object({
  type: z.literal("app_element_mention"),
  element: SerializedElementSelection,
});
export type MessageAppElementMentionBlock = z.infer<
  typeof MessageAppElementMentionBlock
>;

export const MessageTableMentionBlock = z.object({
  type: z.literal("table_mention"),
  tableId: DataSourceTableId,
  tableName: z.string().optional(),
});
export type MessageTableMentionBlock = z.infer<typeof MessageTableMentionBlock>;

export const MessageDataframeMentionBlock = z.object({
  type: z.literal("dataframe_mention"),
  dataframeName: MentionedDataframeName,
});
export type MessageDataframeMentionBlock = z.infer<
  typeof MessageDataframeMentionBlock
>;

export const MessageSemanticDatasetMentionBlock = z.object({
  type: z.literal("semantic_dataset_mention"),
  semanticDatasetId: SemanticDatasetId,
  semanticDatasetName: z.string().optional(),
  semanticDatasetTitle: z.string().optional(),
  semanticProjectId: SemanticProjectId.optional(),
});
export type MessageSemanticDatasetMentionBlock = z.infer<
  typeof MessageSemanticDatasetMentionBlock
>;

export const MessageSemanticViewMentionBlock = z.object({
  type: z.literal("semantic_view_mention"),
  semanticViewId: SemanticViewId,
  semanticViewName: z.string().optional(),
  semanticViewTitle: z.string().optional(),
  semanticProjectId: SemanticProjectId.optional(),
});
export type MessageSemanticViewMentionBlock = z.infer<
  typeof MessageSemanticViewMentionBlock
>;

export const MessageProjectMentionBlock = z.object({
  type: z.literal("project_mention"),
  // The mention block is agent-facing so using projectId instead of hexId is less confusing to the agent.
  projectId: HexId,
  projectName: z.string().optional(),
});
export type MessageProjectMentionBlock = z.infer<
  typeof MessageProjectMentionBlock
>;

export const MessageDataConnectionMentionBlock = z.object({
  type: z.literal("data_connection_mention"),
  dataConnectionId: DataConnectionId,
});
export type MessageDataConnectionMentionBlock = z.infer<
  typeof MessageDataConnectionMentionBlock
>;

/**
 * File attached directly to the prompt (used by ask agent, threads).
 * Contains only the filename since these are uploads to be performed on prompt submission.
 */
export const MessageFileAttachmentBlock = z.object({
  type: z.literal("file_attachment"),
  fileName: z.string(),
});
export type MessageFileAttachmentBlock = z.infer<
  typeof MessageFileAttachmentBlock
>;

/**
 * File mentioned via @ context selector (used by notebook agent).
 * References a project file by ID, allowing content retrieval via GET_FILE_CONTENTS tool.
 */
export const MessageFileMentionBlock = z.object({
  type: z.literal("file_mention"),
  fileId: FileId,
});
export type MessageFileMentionBlock = z.infer<typeof MessageFileMentionBlock>;

/**
 * @deprecated - It is assumed that the user accepted the edit so we only need to store rejected edits.
 *
 * A block that represents an acceptance of agent edits.
 */
export const MessageAcceptBlock = z.object({
  type: z.literal("acceptCellEdit"),
  staticCellId: StaticCellId.or(z.null()).optional(),
  /** @deprecated Use staticCellId instead. */
  cellId: CellId.or(z.null()).optional(),
});
export type MessageAcceptBlock = z.infer<typeof MessageAcceptBlock>;

/**
 * A block that represents a rejection of agent edits.
 */
export const MessageRejectBlock = z.object({
  type: z.literal("rejectCellEdit"),
  staticCellId: StaticCellId.or(z.null()).optional(),
  /** @deprecated Use staticCellId instead. */
  cellId: CellId.or(z.null()).optional(),
});
export type MessageRejectBlock = z.infer<typeof MessageRejectBlock>;

/**
 * A block that represents a rejection of semantic project file edits.
 */
export const MessageRejectSemanticEditBlock = z.object({
  type: z.literal("rejectSemanticEdit"),
  semanticProjectFileId: SemanticProjectFileId.or(z.null()).optional(),
  filePath: SemanticProjectFilePath.or(z.null()).optional(),
});
export type MessageRejectSemanticEditBlock = z.infer<
  typeof MessageRejectSemanticEditBlock
>;

export const ErrorBlock = z.object({
  type: z.literal("error"),
  message: z.string(),
  errorType: z.enum(["cancelled", "fallback", "handled"]),
});
export type ErrorBlock = z.infer<typeof ErrorBlock>;

// Special block type used to separate thinking blocks when in some contexts
// such as MCP.
// Handled by default case in rendering (returns null, invisible)
export const BlockSeparatorBlock = z.object({
  type: z.literal("block_separator"),
});
export type BlockSeparatorBlock = z.infer<typeof BlockSeparatorBlock>;

export const MessageContentBlock = z.union([
  MessageThinkingBlock,
  MessageTextBlock,
  MessageCompressionTriggeredBlock,
  MessageServerCompactionBlock,
  MessageToolCallBlock,
  MessageToolResultBlock,
  ErrorBlock,
  CellMentionBlock,
  ViewingAppCellMentionBlock,
  MessageAppElementMentionBlock,
  MessageTableMentionBlock,
  MessageDataframeMentionBlock,
  MessageSemanticDatasetMentionBlock,
  MessageSemanticViewMentionBlock,
  MessageProjectMentionBlock,
  MessageDataConnectionMentionBlock,
  MessageAcceptBlock,
  MessageRejectBlock,
  MessageRejectSemanticEditBlock,
  MessageFileAttachmentBlock,
  MessageFileMentionBlock,
  MessageAskUserBlock,
  BlockSeparatorBlock,
]);
export type MessageContentBlock = z.infer<typeof MessageContentBlock>;

/**
 * A simpler representation of a chat thread message for evals
 */
export const ConversationMessage = z.object({
  role: ConversationMessageRoleLiteral,
  content: z.array(MessageContentBlock),
  agentInstruction: z.string().or(z.null()).optional(),
});
export type ConversationMessage = z.infer<typeof ConversationMessage>;

// The source of the agent chat thread
export const AgentChatMessageSourceLiteral = z.enum([
  "HEX",
  "SLACK",
  "MCP",
  "TASK",
  "PUBLIC_API",
  "CUSTOMER_EVAL",
]);
export type AgentChatMessageSource = z.infer<
  typeof AgentChatMessageSourceLiteral
>;
export const AgentChatMessageSource = getNormalEnum(
  AgentChatMessageSourceLiteral,
);

/**
 * Debug information attached to agent chat messages for troubleshooting.
 * This metadata can be copied and sent to support for debugging agent issues.
 */
export const AgentChatMessageDebugInfo = z.object({
  agentChatThreadId: AgentChatThreadId,
  hexId: HexId.nullish(),
  semanticProjectId: SemanticProjectId.nullish(),
  // `z.lazy` because this module and `./threads` import each other; reading the
  // enum at module-eval time would hit the uninitialized binding.
  agentChatThreadType: z.lazy(() => z.nativeEnum(AgentChatThreadType)),
  traceId: z.string(),
  /** Only present if running in Temporal workflow */
  workflowId: z.string().optional(),
  workflowStartFailed: z.literal(true).optional(),
  agentId: z.string().optional(),
  agentVersion: z.string().optional(),
});
export type AgentChatMessageDebugInfo = z.infer<
  typeof AgentChatMessageDebugInfo
>;
